// Custom JSON reviver wrapper for DateTime & UTCDate.
// Matches only very specific patterns produced by our serializer to avoid false positives.
//
// DateTime persistence format (serializeDateTime):
//   New format (Temporal-like):
//     <utcIso>|<temporalZonedDateTime>
//     Example: 2025-09-05T08:15:30.123Z|2025-09-05T10:15:30.123+02:00[Europe/Berlin]
//     Example (UTC): 2025-09-05T08:15:30.123Z|2025-09-05T08:15:30.123Z
//   Legacy format (still supported for backward compatibility):
//     <utcIso>|<offset>|<zone>
//     Example: 2025-09-05T08:15:30.123Z|+02:00|Europe/Berlin
//     Example (offset 0): 2025-09-05T08:15:30.123Z|Z|UTC
// UTCDate JSON format:
//   YYYY-MM-DD
//
// Usage: call (<any>JSON).useDateParser() first so the existing behavior (native Date objects)
// runs, then wrap with this reviver to materialize DateTime / UTCDate instances.
// Legacy ISO UTC strings are mapped: midnight -> UTCDate, other times -> DateTime (UTC offset 0).
//
// NOTE: Keep patterns strict to avoid converting user provided free form strings.

import { DateTime } from "./date-time";
import { UTCDate } from "./utc-date";

// correctly detect Date objects without relying on instanceof (which can fail across realms)
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { isDate } from "lodash";

// Strict regex for new Temporal-like persisted DateTime string
// Format: <utcIso>|<temporalZonedDateTime>
// Example: 2025-09-05T08:15:30.123Z|2025-09-05T10:15:30.123+02:00[Europe/Berlin]
const NEW_PERSISTED_DATE_TIME_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z\|\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(Z|[+-]\d{2}:\d{2})(?:\[[^\]]+\])?$/;
// Strict regex for legacy persisted DateTime string (offset may be Z or ±HH:MM)
const LEGACY_PERSISTED_DATE_TIME_REGEX =
  /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}:\d{2}\.\d{3}Z\|(Z|[+-]\d{2}:\d{2})\|[^|]+$/;
// Strict regex for pure date (YYYY-MM-DD)
const UTC_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
// Legacy ISO UTC detection (classic JSON date strings without persistence pipes)
const LEGACY_ISO_UTC_REGEX = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{3}))?)?Z$/;

function reviveLeaf(value: unknown): unknown {
  if (typeof value !== "string") return value;
  // New Temporal-like format: <utcIso>|<temporalZonedDateTime>
  if (NEW_PERSISTED_DATE_TIME_REGEX.test(value)) {
    try {
      return DateTime.deserialize(value);
    } catch {
      return value;
    }
  }
  // Legacy format: <utcIso>|<offset>|<zone>
  if (LEGACY_PERSISTED_DATE_TIME_REGEX.test(value)) {
    try {
      return DateTime.deserialize(value);
    } catch {
      return value;
    }
  }
  if (UTC_DATE_REGEX.test(value)) {
    try {
      return UTCDate.parse(value);
    } catch {
      return value;
    }
  }
  const legacy = LEGACY_ISO_UTC_REGEX.exec(value);
  if (legacy) {
    const [, y, M, d, hh, mm, ss = "00", frac = "000"] = legacy;
    const isMidnight = hh === "00" && mm === "00" && ss === "00" && /^0+$/.test(frac);
    if (isMidnight) {
      try {
        return UTCDate.parse(`${y}-${M}-${d}`);
      } catch {
        return value;
      }
    }
    // Non-midnight legacy ISO UTC string -> DateTime with offset 0 and UTC zone
    try {
      const epoch = Date.parse(value);
      return DateTime.fromEpoch(epoch, 0, "UTC");
    } catch {
      return value;
    }
  }
  return value;
}

function deepRevive(node: any): any {
  if (node == null) return node;
  if (Array.isArray(node)) return node.map(deepRevive);
  if (typeof node === "object") {
    if (node instanceof DateTime || node instanceof UTCDate) return node;
    if (isDate(node)) {
      // Convert legacy ISO midnight Date objects to UTCDate; non-midnight -> DateTime
      const iso = node.toISOString();
      const match = LEGACY_ISO_UTC_REGEX.exec(iso);
      if (match) {
        const [, y, M, d, hh, mm, ss = "00", frac = "000"] = match;
        const isMidnight = hh === "00" && mm === "00" && ss === "00" && /^0+$/.test(frac);
        if (isMidnight) return UTCDate.parse(`${y}-${M}-${d}`);
        try {
          return DateTime.fromEpoch(node.getTime(), 0, "UTC");
        } catch {
          return node; // fallback keep Date
        }
      }
      return node; // keep other Date instances
    }
    // Handle legacy serialized DateTime plain objects stored before toJSON() was added.
    // Old format: { epochMillis: number, offsetMinutes: number, zoneId?: string }
    if (Number.isFinite(node.epochMillis) && Number.isFinite(node.offsetMinutes)) {
      try {
        return DateTime.fromEpoch(node.epochMillis, node.offsetMinutes, node.zoneId ?? undefined);
      } catch {
        return node;
      }
    }
    for (const key of Object.keys(node)) {
      const v = node[key];
      const revived = reviveLeaf(v);
      node[key] = deepRevive(revived);
    }
    return node;
  }
  return reviveLeaf(node);
}

let installed = false;
let originalParseRef: typeof JSON.parse | null = null;

export function installDateTimeAndUTCDateReviver(): void {
  if (installed) return;
  installed = true;
  const originalParse: typeof JSON.parse = JSON.parse as any;
  originalParseRef = originalParse;
  (JSON as any)._originalParseWithDateTime = originalParse;
  JSON.parse = function patchedParse(text: string, reviver?: (this: any, key: string, value: any) => any): any {
    const intermediate = originalParse(text, reviver);
    return deepRevive(intermediate);
  } as any;
}

export function resetDateTimeAndUTCDateReviver(): void {
  if (!installed) return;
  if (originalParseRef) {
    JSON.parse = originalParseRef as any;
  }
  installed = false;
}
